import { ReactNode, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { createFileRoute, Navigate, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { Heading, Text } from 'react-aria-components';
import PageTitle from 'src/components/creator/PageTitle';
import RouterButton from 'src/components/ui/RouterButton';
import RequestOrderForm from 'src/components/creator/RequestOrderForm';
import PageLoader from 'src/components/ui/PageLoader';
import REQUEST_QUERY from 'src/graphql/query/order-request.graphql';
import CheckIcon from 'src/assets/icons/shield-check.svg';
import useUser from 'src/hooks/useUser';
import { ClientType } from 'src/types/client';
import {
  BindingType,
  ContactType,
  CoverType,
  OrderRequest,
  OrderType,
  PrintType,
  ProductionType,
  RequestOwnMaterials,
  RequestStatus,
} from 'src/types/order';
import { RequestOrderSchemaType } from 'src/schema/order-request';
import { mapRequestToFormMaterials } from 'src/components/creator/RequestOrderForm/materials-utils';

const EditOrderRequestPage = () => {
  const { requestId } = Route.useParams();
  const { reOrder } = Route.useSearch();
  const [successData, setSuccessData] =
    useState<Partial<RequestOrderSchemaType> | null>(null);

  const { user, loading: userLoading } = useUser();

  const { data, loading, error } = useQuery<{
    request: OrderRequest;
  }>(REQUEST_QUERY, {
    variables: { requestId },
  });

  const initialValues = useMemo(() => {
    const bookstoreId = data?.request?.bookstoreId;

    const isOrganizationMember = [
      ClientType.CreatorOrgAdmin,
      ClientType.CreatorOrgRepresentative,
    ].includes(user?.clientType);

    const professorIds = (data?.request?.professors ?? []).map((p) => ({
      value: p.professorId,
      label: p.name,
    })) as [];

    const materials = mapRequestToFormMaterials(data?.request?.materials ?? []);

    return {
      creatorDetails: {
        email: data?.request?.contactEmail ?? '',
        phone: data?.request?.contactPhone ?? '',
        contactName: data?.request?.contactName ?? '',
        contactType: data?.request?.contactType ?? ContactType.Other,
        professorIds,
        bookstoreId: String(bookstoreId ?? ''),
        institutionId: String(data?.request?.institution?.institutionId ?? ''),
        isBookstore: isOrganizationMember,
        emptyBookstore: !bookstoreId,
        class: data?.request?.class ?? '',
      },
      orderDetails: {
        orderType: reOrder
          ? OrderType.ReOrder
          : (data?.request?.orderType ?? OrderType.New),
        productionType: data?.request?.productionType ?? ProductionType.Digital,
        digitalPlatform: data?.request?.digitalPlatform ?? '',
        print: data?.request?.printing ?? PrintType.SingleSided,
        binding: data?.request?.binding ?? BindingType.Coil,
        productionProperties: (data?.request?.productionProperties ?? []) as [],
        cover: data?.request?.coverPage ?? CoverType.SchoolDiscipline,
        instructions: data?.request?.productionInstructions ?? '',
        otherPlatform: data?.request?.otherDigitalPlatform ?? '',
        isbn: data?.request?.isbn ?? '',
      },
      courseDetails: {
        name: data?.request?.courseName ?? '',
        number: data?.request?.courseNumber ?? '',
        termId: String(data?.request?.term?.termId ?? ''),
        enrollment: String(data?.request?.estimatedEnrollment ?? ''),
        deskCopies: String(data?.request?.professorDeskCopies ?? ''),
        orderQty: String(data?.request?.orderQty ?? ''),
        poNumber: data?.request?.poNumber ?? '',
        poPdfFile: null, // TODO: hook when BE is ready
        dueDate: data?.request?.dueDateInBookstore ?? '',
        classStartDate: data?.request?.firstDayOfClass ?? '',
      },
      copyrights: {
        ownMaterial: data?.request?.ownMaterials ?? RequestOwnMaterials.Own,
        ladLibrary: Boolean(data?.request?.addToLadLibrary),
        wantsRoyalties: Boolean(data?.request?.wantsRoyalties),
        royaltiesAmount: data?.request?.royaltiesAmount
          ? parseFloat(data.request.royaltiesAmount).toString()
          : '',
      },
      materialsData: {
        materials,
      },
      additional: {
        description: data?.request?.specialInstructions ?? '',
      },
    };
  }, [data?.request, reOrder, user?.clientType]);

  if (loading || userLoading) return <PageLoader />;
  if (error) return <Navigate to="/requests" />;
  if (!reOrder && data?.request?.status !== RequestStatus.New)
    return <Navigate to="/requests" />;

  if (successData)
    return (
      <Container>
        <SuccessScreen data={successData} />
      </Container>
    );

  return (
    <RequestOrderForm
      isReorder={Boolean(reOrder)}
      requestId={String(data?.request.requestId)}
      initialValues={initialValues}
      onSuccess={(r) => setSuccessData(r)}
    />
  );
};

const Container = ({ children }: { children: ReactNode }) => (
  <>
    <PageTitle title="Edit Order" />
    <div className="relative overflow-hidden bg-surface-alt rounded-xl shadow-3 mt-4 p-6 flex-1 flex items-center justify-center">
      {children}
    </div>
  </>
);

const SuccessScreen = ({ data }: { data: Partial<RequestOrderSchemaType> }) => {
  return (
    <div className="max-w-98 text-center text-base font-medium text-surface-alt-90">
      <CheckIcon className="inline-block w-20 h-20" />
      <Heading level={5} className="text-2xl font-bold mt-9 mb-4">
        Thank you for your order!
      </Heading>
      <Text elementType="p">
        It has been successfully submitted and is currently being processed.
        Your account manager will be in touch soon.
      </Text>

      <hr className="my-7 border-surface-10" />

      <Text
        elementType="strong"
        className="text-surface-alt-90 text-lg font-bold"
      >
        {data.courseDetails?.name ?? ''}
      </Text>
      <Text
        elementType="span"
        className="block my-0.5 text-surface-alt-75 text-md font-semibold"
      >
        {data.courseDetails?.number ?? ''}
      </Text>
      <Text
        elementType="span"
        className="text-surface-alt-90 text-md font-normal"
      >
        {data.creatorDetails?.professorIds.map((p) => p.label).join(', ')}
      </Text>

      <div className="bg-surface-alt absolute bottom-0 left-0 right-0 flex items-center justify-end gap-2.5 p-4 pt-3">
        <RouterButton
          to="/requests"
          buttonProps={{ variant: 'surface', ghost: true }}
        >
          Go to Order Requests
        </RouterButton>

        <RouterButton
          to="/requests/create"
          buttonProps={{ variant: 'secondary' }}
        >
          Request New Order
        </RouterButton>
      </div>
    </div>
  );
};

const paramsSchema = z.object({
  requestId: z.coerce.number().int().positive(),
});

const EditRequestSchema = z.object({
  reOrder: z.boolean().optional(),
});

export const Route = createFileRoute(
  '/_authenticated/_creator/requests/$requestId/edit'
)({
  beforeLoad: async ({ params }) => {
    const result = paramsSchema.safeParse(params);
    if (!result.success) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/requests' });
    }
  },
  component: EditOrderRequestPage,
  validateSearch: (search: Record<string, unknown>) =>
    EditRequestSchema.parse(search),
});
